import { installEqualWebLaunchButtonOffscreenStyle } from './accessibility';

// o VLibras é aberto pelo botão flutuante do próprio plugin do gov.br (vlibras-portal 7.x);
// não há item do VLibras no menu da EqualWeb
const VLIBRAS_PLUGIN_SRC = 'https://vlibras.gov.br/app/vlibras-plugin.js';
const VLIBRAS_APP_BASE = 'https://vlibras.gov.br/app';

const WIDGET_READY_TIMEOUT_MS = 15000;

type WidgetCtor = new (appBaseUrl: string) => unknown;

let widgetReadyPromise: Promise<void> | null = null;
let equalWebFabOnlyScheduled = false;

function waitUntil<T>(
  predicate: () => T | null | undefined,
  options: { timeoutMs: number; intervalMs: number },
): Promise<T | null> {
  return new Promise(resolve => {
    const start = Date.now();
    const tick = () => {
      try {
        const value = predicate();
        if (value) {
          resolve(value);
          return;
        }
      } catch {} // eslint-disable-line no-empty

      if (Date.now() - start >= options.timeoutMs) {
        resolve(null);
        return;
      }

      window.setTimeout(tick, options.intervalMs);
    };
    tick();
  });
}

function loadPluginScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${VLIBRAS_PLUGIN_SRC}"]`,
    );

    if (existing) {
      if (existing.dataset.vlibrasLoaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Falha ao carregar vlibras-plugin.js')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = VLIBRAS_PLUGIN_SRC;
    script.async = true;
    script.addEventListener(
      'load',
      () => {
        script.dataset.vlibrasLoaded = 'true';
        resolve();
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => reject(new Error('Falha ao carregar vlibras-plugin.js')),
      { once: true },
    );
    document.body.appendChild(script);
  });
}

function resolveWidgetCtor(): WidgetCtor | null {
  const raw = ((window as unknown) as { VLibras?: any }).VLibras;
  if (!raw) return null;
  if (typeof raw.Widget === 'function') return raw.Widget as WidgetCtor;
  if (typeof raw.default?.Widget === 'function') return raw.default.Widget as WidgetCtor;
  return null;
}

function ensureEqualWebFabOnly(): void {
  installEqualWebLaunchButtonOffscreenStyle();
  if (equalWebFabOnlyScheduled) return;
  equalWebFabOnlyScheduled = true;
  [500, 1500, 3500, 8000].forEach(ms => {
    window.setTimeout(installEqualWebLaunchButtonOffscreenStyle, ms);
  });
}

export function initVlibras(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  ensureEqualWebFabOnly();

  if (widgetReadyPromise) return widgetReadyPromise;

  widgetReadyPromise = (async () => {
    await loadPluginScript();

    const Ctor = await waitUntil(resolveWidgetCtor, {
      timeoutMs: WIDGET_READY_TIMEOUT_MS,
      intervalMs: 100,
    });
    if (!Ctor) {
      throw new Error('window.VLibras.Widget não ficou disponível a tempo.');
    }

    const previousOnload = window.onload;
    new Ctor(VLIBRAS_APP_BASE); // eslint-disable-line no-new

    const widgetOnload = window.onload;
    const pageAlreadyLoaded = document.readyState === 'complete';

    if (
      pageAlreadyLoaded &&
      typeof widgetOnload === 'function' &&
      widgetOnload !== previousOnload
    ) {
      Promise.resolve()
        .then(() => (widgetOnload as EventListener).call(window, new Event('load')))
        .catch(() => undefined);
    }
  })();

  widgetReadyPromise.catch(() => {
    widgetReadyPromise = null;
  });

  return widgetReadyPromise;
}
